import { RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import { Fragment, type MouseEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

import { Button } from "~/components/ui";
import type { GCP } from "~/features/georeferencer/utils";

interface ImageViewerProps {
  imageUrl: string | null;
  onImageClick?: (x: number, y: number) => void;
  selectedPoint?: { x: number; y: number } | null;
  gcps?: GCP[];
  selectedGcpId?: string | null;
}

/** Marker colors are functional data overlays (selected / normal / pending), not chrome. */
const MARKER_SELECTED = "var(--score-a)";
const MARKER_NORMAL = "var(--fg-3)";
const MARKER_PENDING = "var(--danger)";

export function ImageViewer({
  imageUrl,
  onImageClick,
  selectedPoint,
  gcps = [],
  selectedGcpId = null,
}: ImageViewerProps) {
  const { t } = useTranslation();
  const image = useRef<HTMLImageElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const transform = useRef<ReactZoomPanPinchRef>(null);
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null);
  const isClickAllowed = useRef(true);

  const handleImageLoad = () => {
    if (image.current) {
      setImageSize({ width: image.current.naturalWidth, height: image.current.naturalHeight });
    }
  };

  const handleImageClick = (e: MouseEvent<HTMLImageElement>) => {
    if (!onImageClick || !image.current || !container.current || !isClickAllowed.current) return;

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const relativeClickX = (e.clientX - rect.left) / rect.width;
    const relativeClickY = (e.clientY - rect.top) / rect.height;
    const x = Math.round(relativeClickX * imageSize.width);
    const y = Math.round(relativeClickY * imageSize.height);

    if (relativeClickX >= 0 && relativeClickX <= 1 && relativeClickY >= 0 && relativeClickY <= 1) {
      onImageClick(x, y);
    }
  };

  return (
    <div className="flex h-full w-full flex-col rounded-[var(--radius-md)] border border-border-default bg-bg-surface p-3">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-16-bold capitalize text-fg-1">{t("ui.sourceX", { x: t("ui.image") })}</h2>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t("ui.zoomIn")}
            onClick={() => transform.current?.zoomIn(0.2)}
          >
            <ZoomIn />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t("ui.zoomOut")}
            onClick={() => transform.current?.zoomOut(0.2)}
          >
            <ZoomOut />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t("ui.resetZoom")}
            onClick={() => transform.current?.resetTransform()}
          >
            <RotateCcw />
          </Button>
        </div>
      </div>
      <div ref={container} className="relative flex flex-grow items-center justify-center overflow-hidden bg-bg-subtle">
        {imageUrl ? (
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={8}
            limitToBounds={false}
            centerOnInit={true}
            panning={{ disabled: false }}
            wheel={{ disabled: false, step: 0.2 }}
            zoomAnimation={{ disabled: true }}
            doubleClick={{ disabled: true }}
            ref={transform}
            onPanningStart={() => {
              isClickAllowed.current = false;
            }}
            onWheelStop={() => {
              setTimeout(() => {
                isClickAllowed.current = true;
              }, 50);
            }}
          >
            {() => (
              <Fragment>
                <TransformComponent
                  wrapperStyle={{ width: "100%", height: "100%", pointerEvents: "none" }}
                  contentStyle={{ pointerEvents: "none" }}
                  wrapperProps={{
                    onMouseDown: (e: MouseEvent<HTMLDivElement>) => {
                      mouseDownPos.current = { x: e.clientX, y: e.clientY };
                      isClickAllowed.current = true;
                    },
                    onMouseUp: (e: MouseEvent<HTMLDivElement>) => {
                      if (mouseDownPos.current) {
                        const deltaX = Math.abs(mouseDownPos.current.x - e.clientX);
                        const deltaY = Math.abs(mouseDownPos.current.y - e.clientY);
                        isClickAllowed.current = !(deltaX > 5 || deltaY > 5);
                      }
                    },
                  }}
                >
                  <img
                    ref={image}
                    src={imageUrl}
                    alt={t("ui.sourceX", { x: t("ui.image") })}
                    onLoad={handleImageLoad}
                    onClick={(e) => {
                      if (isClickAllowed.current) handleImageClick(e);
                    }}
                    className="max-h-full max-w-full object-contain"
                    style={{ cursor: "crosshair", pointerEvents: "auto" }}
                  />

                  {gcps.map((gcp) => (
                    <span
                      key={gcp.id}
                      className="pointer-events-none absolute z-[5] h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                      style={{
                        backgroundColor: gcp.id === selectedGcpId ? MARKER_SELECTED : MARKER_NORMAL,
                        left: `${(gcp.sourceX / imageSize.width) * 100}%`,
                        top: `${(gcp.sourceY / imageSize.height) * 100}%`,
                      }}
                    />
                  ))}

                  {selectedPoint && (
                    <span
                      className="pointer-events-none absolute z-10 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                      style={{
                        backgroundColor: MARKER_PENDING,
                        left: `${(selectedPoint.x / imageSize.width) * 100}%`,
                        top: `${(selectedPoint.y / imageSize.height) * 100}%`,
                      }}
                    />
                  )}
                </TransformComponent>
              </Fragment>
            )}
          </TransformWrapper>
        ) : null}
      </div>
    </div>
  );
}
